import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Check, ArrowRight } from "lucide-react";
import { submitContact } from "../lib/api";
import { readMarketingConsent, readMetaCookies } from "../lib/consent";

const DK_PHONE = /^(\+?45[ -]?)?(\d{2}[ -]?){3}\d{2}$/;

function generateEventId() {
    const ts = Math.floor(Date.now() / 1000);
    const rand = Math.random().toString(36).slice(2, 6).padEnd(4, "0");
    return `lead_${ts}_${rand}`;
}

/**
 * TrialForm — shared 14-day trial signup form used by /14-dages-proeve.
 *
 * Fires the same GTM events as the trial form on /beregn-pris:
 *   • form_start   — first focus in any field
 *   • form_submit  — only after server confirmation, with event_id
 *
 * Sends event_id + marketing_consent + fbp/fbc to the backend so
 * `/api/contact` can fire a Meta CAPI `Lead` event with matching event_id
 * (browser pixel + server pixel deduplicate on Meta's side).
 *
 * `source` prop is included in the message body so admin can distinguish
 * leads across landing pages in /admin/contacts.
 */
export default function TrialForm({ source = "14-dages-proeve" }) {
    const [form, setForm] = useState({ name: "", company: "", email: "", phone: "" });
    const [errors, setErrors] = useState({});
    const [accepted, setAccepted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const startedRef = useRef(false);

    const pushFormStart = () => {
        if (startedRef.current) return;
        startedRef.current = true;
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: "form_start" });
    };

    const validate = () => {
        const e = {};
        if (!form.name.trim()) e.name = "Skriv jeres navn.";
        if (!form.company.trim()) e.company = "Skriv firmanavn.";
        if (!form.email.trim() || !form.email.includes("@")) e.email = "Skriv en gyldig e-mail.";
        if (!DK_PHONE.test(form.phone.trim())) e.phone = "Skriv et dansk telefonnummer.";
        if (!accepted) e.consent = "Du skal acceptere at vi må kontakte jer.";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const onSubmit = async (ev) => {
        ev.preventDefault();
        if (!validate()) return;
        setSubmitting(true);
        const eventId = generateEventId();
        const marketingConsent = readMarketingConsent();
        const { fbp, fbc } = readMetaCookies();
        try {
            await submitContact({
                ...form,
                message: `14-dages gratis prøveperiode — startet via /${source}`,
                event_id: eventId,
                marketing_consent: marketingConsent,
                fbp,
                fbc,
            });
            setDone(true);
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: "form_submit",
                event_id: eventId,
            });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Noget gik galt. Prøv igen.");
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div className="text-center py-8" data-testid="trial-form-success">
                <div className="h-14 w-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto">
                    <Check className="h-7 w-7" />
                </div>
                <h3 className="mt-4 font-display text-2xl font-bold text-slate-900">Tak. Vi er i gang.</h3>
                <p className="mt-3 text-slate-600 leading-relaxed max-w-md mx-auto">
                    I hører fra os inden for én arbejdsdag. Vi ringer kort, opretter kontoen og flytter jeres data ind, så I kan bruge de 14 dage på systemet i stedet for på opsætning.
                </p>
            </div>
        );
    }

    const fields = [
        { k: "name", label: "Navn", type: "text", auto: "name" },
        { k: "company", label: "Firma", type: "text", auto: "organization" },
        { k: "email", label: "E-mail", type: "email", auto: "email" },
        { k: "phone", label: "Telefon", type: "tel", auto: "tel" },
    ];

    return (
        <form onSubmit={onSubmit} className="space-y-4" data-testid="trial-form" noValidate>
            <div className="grid sm:grid-cols-2 gap-3">
                {fields.map((f) => (
                    <div key={f.k} className={f.k === "email" || f.k === "phone" ? "sm:col-span-1" : "sm:col-span-1"}>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            {f.label}
                        </label>
                        <input
                            type={f.type}
                            autoComplete={f.auto}
                            value={form[f.k]}
                            onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                            onFocus={pushFormStart}
                            data-testid={`trial-${f.k}`}
                            className={`w-full rounded-lg border px-4 h-11 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-colors ${
                                errors[f.k] ? "border-rose-400 bg-rose-50" : "border-slate-200"
                            }`}
                        />
                        {errors[f.k] && (
                            <p className="mt-1 text-xs text-rose-600">{errors[f.k]}</p>
                        )}
                    </div>
                ))}
            </div>
            <label className="flex items-start gap-2.5 pt-1 cursor-pointer">
                <input
                    type="checkbox"
                    checked={accepted}
                    onChange={(e) => setAccepted(e.target.checked)}
                    data-testid="trial-consent"
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                />
                <span className="text-xs text-slate-600 leading-relaxed">
                    Jeg accepterer at Cleany må kontakte mig ifm. opstart af prøven, og at data behandles i henhold til <a href="/privatlivspolitik" className="underline">privatlivspolitikken</a>.
                </span>
            </label>
            {errors.consent && (
                <p className="text-xs text-rose-600">{errors.consent}</p>
            )}
            <button
                type="submit"
                disabled={submitting}
                data-testid="trial-submit"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-sky-600 hover:bg-sky-700 disabled:bg-sky-400 text-white font-semibold h-12 shadow-lg shadow-sky-600/25 transition-all active:scale-[0.98]"
            >
                {submitting ? (
                    <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Sender...
                    </>
                ) : (
                    <>
                        Start 14 dages gratis prøve
                        <ArrowRight className="h-4 w-4" />
                    </>
                )}
            </button>
            <p className="text-center text-xs text-slate-500">
                Ingen kreditkort · Ingen binding · Data på EU-servere
            </p>
        </form>
    );
}
